import React, {useState} from 'react';
import {Trans, useTranslation} from 'react-i18next';

import {SettingsPanel} from '@pexip/media-components';
import {
    ThemeProvider,
    ToggleSwitch,
    Text,
    FontVariant,
    Input,
} from '@pexip/components';

import {SettingsActionsRow} from '../components/SettingsActionsRow';

import {TestId} from '../../test/testIds';
import {config, useConfig} from '../config';
import {useAmIApiParticipant} from '../hooks/useAmIApiParticipant';
import {useInfinityContext} from '../hooks/useInfinityContext';
import {useIsRegistered} from '../hooks/registration';

export const JoiningSettings: React.FC = () => {
    const {t} = useTranslation();

    const [enforceAudioMute, setEnforceAudioMute] =
        useConfig('enforceAudioMute');
    const [enforceVideoMute, setEnforceVideoMute] =
        useConfig('enforceVideoMute');
    const [skipDisplayNamePrompt, setSkipDisplayNamePrompt] = useConfig(
        'skipDisplayNamePrompt',
    );

    const [name, setName] = useState(config.get('displayName'));
    const isRegistered = useIsRegistered();

    const infinity = useInfinityContext();
    const amIApiParticipant = useAmIApiParticipant(infinity.getMe);

    const handleNameBlur = () => {
        setName(config.get('displayName'));
    };

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setName(e.target.value);

        const displayName = e.target.value.trim();

        if (displayName.length) {
            config.set({key: 'displayName', value: displayName, persist: true});
        }
    };

    return (
        <SettingsPanel>
            <ThemeProvider colorScheme="light">
                <Input
                    isDisabled={isRegistered}
                    autoComplete="name"
                    value={name}
                    className="mb-2"
                    label={t('username.your-name', 'Your name')}
                    name="name"
                    onChange={handleNameChange}
                    onBlur={handleNameBlur}
                    placeholder={t(
                        'username.name-input-placeholder',
                        "e.g. 'David'",
                    )}
                    sizeModifier="medium"
                    data-testid={TestId.SettingsUserNameStepTextInput}
                />
                <Text
                    fontVariant={FontVariant.Small}
                    variant="tinted"
                    data-testid={TestId.SettingsUserNameStepTextInputDisclaimer}
                >
                    <Trans i18nKey="settings.joining-name-disclaimer">
                        To update your display name during an ongoing call, you
                        will need to re-join the call.
                    </Trans>
                </Text>
                {!isRegistered && (
                    <SettingsActionsRow
                        title={t(
                            'settings.skip-display-name-prompt-title',
                            'Skip name step when joining',
                        )}
                        subtitle={t(
                            'settings.skip-display-name-prompt-desc',
                            'Use your saved display name each time you join a meeting that requires one. When turned off, you will be asked to enter your name each time.',
                        )}
                        data-testid={TestId.ToggleSkipDisplayNamePromptRow}
                    >
                        <div style={{paddingInlineStart: '50px'}}>
                            <ToggleSwitch
                                onChange={isChecked => {
                                    setSkipDisplayNamePrompt(isChecked, true);
                                }}
                                label={t(
                                    'settings.skip-display-name-prompt-title',
                                    'Skip name step when joining',
                                )}
                                labelModifier="hidden"
                                name="skip-display-name-prompt"
                                data-testid={TestId.ToggleSkipDisplayNamePrompt}
                                checked={skipDisplayNamePrompt}
                            />
                        </div>
                    </SettingsActionsRow>
                )}
                {!amIApiParticipant && (
                    <SettingsActionsRow
                        title={t(
                            'settings.enforce-mute-camera-by-default-title',
                            'Camera off when joining',
                        )}
                        subtitle={t(
                            'settings.enforce-mute-camera-by-default-desc',
                            'When you join the meeting, your camera will be off each time you join. You can turn the camera on at anytime during the meeting.',
                        )}
                        data-testid={TestId.ToggleEnforceVideoMuteRow}
                    >
                        <div style={{paddingInlineStart: '50px'}}>
                            <ToggleSwitch
                                onChange={isChecked => {
                                    setEnforceVideoMute(isChecked, true);
                                }}
                                label={t(
                                    'settings.enforce-mute-camera-by-default-title',
                                    'Camera off when joining',
                                )}
                                labelModifier="hidden"
                                name="enforce-video-mute"
                                data-testid={TestId.ToggleEnforceVideoMute}
                                checked={enforceVideoMute}
                            />
                        </div>
                    </SettingsActionsRow>
                )}
                {!amIApiParticipant && (
                    <SettingsActionsRow
                        title={t(
                            'settings.enforce-mute-microphone-by-default-title',
                            'Mute microphone when joining',
                        )}
                        subtitle={t(
                            'settings.enforce-mute-microphone-by-default-desc',
                            'When you join the meeting, your microphone will be muted each time you join. You can unmute your microphone at anytime during the meeting.',
                        )}
                        data-testid={TestId.ToggleEnforceAudioMuteRow}
                    >
                        <div style={{paddingInlineStart: '50px'}}>
                            <ToggleSwitch
                                onChange={isChecked => {
                                    setEnforceAudioMute(isChecked, true);
                                }}
                                label={t(
                                    'settings.enforce-mute-microphone-by-default-title',
                                    'Mute microphone when joining',
                                )}
                                labelModifier="hidden"
                                name="enforce-audio-mute"
                                data-testid={TestId.ToggleEnforceAudioMute}
                                checked={enforceAudioMute}
                            />
                        </div>
                    </SettingsActionsRow>
                )}
            </ThemeProvider>
        </SettingsPanel>
    );
};
